import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx';

interface FieldBits {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

function useFieldIds(id?: string) {
  const auto = useId();
  const base = id ?? auto;
  return { id: base, hint: `${base}-hint`, error: `${base}-error` };
}

function describedBy(ids: { hint: string; error: string }, hint: unknown, error: unknown) {
  return [hint ? ids.hint : null, error ? ids.error : null].filter(Boolean).join(' ') || undefined;
}

export type TextFieldProps = FieldBits & InputHTMLAttributes<HTMLInputElement>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, id, className, ...rest },
  ref,
) {
  const ids = useFieldIds(id);
  return (
    <div className={cx('fl-field', className)}>
      <label className="fl-label" htmlFor={ids.id}>
        {label}
      </label>
      <input
        ref={ref}
        id={ids.id}
        className="fl-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(ids, hint, error)}
        {...rest}
      />
      {hint ? (
        <span id={ids.hint} className="fl-hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={ids.error} className="fl-error">
          {error}
        </span>
      ) : null}
    </div>
  );
});

export type TextAreaProps = FieldBits & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextArea({ label, hint, error, id, className, ...rest }: TextAreaProps) {
  const ids = useFieldIds(id);
  return (
    <div className={cx('fl-field', className)}>
      <label className="fl-label" htmlFor={ids.id}>
        {label}
      </label>
      <textarea
        id={ids.id}
        className="fl-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(ids, hint, error)}
        {...rest}
      />
      {hint ? (
        <span id={ids.hint} className="fl-hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={ids.error} className="fl-error">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface SwitchProps {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  /** Compact inline switch (toolbars): smaller label weight. */
  size?: 'md' | 'sm';
}

/** ARIA switch. Button element gives Space/Enter activation for free. */
export function Switch({ label, description, checked, onChange, disabled, id, size = 'md' }: SwitchProps) {
  const ids = useFieldIds(id);
  return (
    <button
      type="button"
      role="switch"
      id={ids.id}
      className="fl-switch"
      aria-checked={checked}
      aria-describedby={description ? ids.hint : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="fl-switch-track" aria-hidden="true">
        <span className="fl-switch-thumb" />
      </span>
      <span className="flex flex-col">
        <span className={size === 'sm' ? 'text-sm font-medium' : 'font-semibold'}>{label}</span>
        {description ? (
          <span id={ids.hint} className="fl-hint">
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, id, className, ...rest }: CheckboxProps) {
  const ids = useFieldIds(id);
  return (
    // The whole row is one <label>, not just the text (D25): the box stays a visually small 20px
    // (accent-ink), but the click/tap target is the full min-h-11 (44px) row, including the gap and
    // the description — a phone tap anywhere in the row toggles it, not just the box itself.
    <label className={cx('flex min-h-11 cursor-pointer items-start gap-3 py-1', className)}>
      <input
        id={ids.id}
        type="checkbox"
        className="mt-1 h-5 w-5 flex-none accent-ink"
        // Nesting the description inside the same <label> (for a >=44px click target, D25) would
        // otherwise fold its text into the checkbox's accessible name; aria-labelledby keeps the
        // name to just the label text, aria-describedby keeps the description a description.
        aria-labelledby={`${ids.id}-label`}
        aria-describedby={description ? ids.hint : undefined}
        {...rest}
      />
      <span className="flex flex-col">
        <span id={`${ids.id}-label`} className="font-semibold">
          {label}
        </span>
        {description ? (
          <span id={ids.hint} className="fl-hint">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}
